import { Button } from "@/components/ui/button";
import { FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDocumentViewer } from "./useDocumentViewer";
import { useDocumentGeneration } from "./useDocumentGeneration";
import { useToast } from "@/hooks/use-toast";
import { DocumentViewer } from "@/components/documents/DocumentViewer";
import { useMemo, useState } from "react";
import { AIProgressBar } from "@/components/ai-assistant/AIProgressBar";

export interface DocumentGenerateButtonProps {
  stepTitle: string;
  caseId: string;
  onGenerateDocument?: () => void;
  isLoading?: boolean;
}

export function DocumentGenerateButton({
  stepTitle,
  caseId,
  onGenerateDocument,
  isLoading: externalLoading = false,
}: DocumentGenerateButtonProps) {
  const { toast } = useToast();
  const [progress, setProgress] = useState(0);

  const documentViewerProps = useMemo(() => ({
    stepTitle,
    caseId,
  }), [stepTitle, caseId]);

  const {
    isViewerOpen,
    setIsViewerOpen,
    document,
    handleDownload,
    refetchDocument
  } = useDocumentViewer(documentViewerProps.stepTitle, documentViewerProps.caseId);

  const { generateDocument, isGenerating } = useDocumentGeneration({
    stepTitle,
    caseId,
    onSuccess: async () => {
      await refetchDocument();
      onGenerateDocument?.();
      toast({
        title: "Suksess",
        description: "Dokumentet ble generert",
      });
      setTimeout(() => setProgress(0), 500);
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke generere dokumentet. Vennligst prøv igjen.",
      });
      setProgress(0);
    }
  });

  const loading = isGenerating || externalLoading;

  const handleGenerate = async () => {
    setProgress(25);
    setTimeout(() => setProgress(50), 500);
    setTimeout(() => setProgress(75), 1000);
    await generateDocument();
    setTimeout(() => setProgress(100), 1500);
  };

  if (document) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsViewerOpen(true)}
          className={cn(
            "transition-all duration-200",
            "hover:bg-primary/10 active:scale-95"
          )}
        >
          <FileText className="h-4 w-4 mr-2" />
          Vis dokument
        </Button>

        <DocumentViewer
          document={document}
          isOpen={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          onDownload={handleDownload}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        onClick={handleGenerate}
        disabled={loading}
        variant="outline"
        size="sm"
        className={cn(
          "transition-all duration-200",
          "hover:bg-primary/10 active:scale-95"
        )}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Genererer...
          </>
        ) : (
          <>
            <FileText className="h-4 w-4 mr-2" />
            Generer dokument
          </>
        )}
      </Button>
      {(loading || progress > 0) && (
        <AIProgressBar progress={progress} />
      )}
    </div>
  );
}